import { useCallback, useState } from 'react'
import {
  StyledAccordionItem,
  StyledAccordionItemBody,
  StyledAccordionItemBodyInner,
  StyledAccordionItemHead,
  StyledAccordionItemHeadIcon,
  StyledAccordionItemHeadText,
} from './index.styled'

const OUT_OF_BOUNDS_ITEM_IDX = -1

interface AccordionItemProps {
  id: string
  title: React.ReactNode
  content: React.ReactNode
}

interface AccordionProps {
  items: AccordionItemProps[]
  initialOpenItemIdx?: number
  makeDataId?: (item: AccordionItemProps) => string
  lastFocusElementId?: string
  onItemClick?: (question: string, action: 'open' | 'close') => void // Add optional tracking prop
}

const Accordion = ({
  items,
  makeDataId,
  initialOpenItemIdx = OUT_OF_BOUNDS_ITEM_IDX,
  lastFocusElementId,
  onItemClick,
}: AccordionProps) => {
  const [openItemIdx, setOpenItemIdx] = useState(initialOpenItemIdx)

  const toggleOpen = useCallback(
    (idx: number) => {
      const isOpen = idx === openItemIdx
      setOpenItemIdx(isOpen ? OUT_OF_BOUNDS_ITEM_IDX : idx)

      // Call the tracking function if provided
      if (onItemClick) {
        onItemClick(items[idx].title as string, isOpen ? 'close' : 'open')
      }
    },
    [openItemIdx, items, onItemClick],
  )

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleOpen(idx)
    }

    // If Tab is pressed on the last item, move focus to the close button using `data-id`
    if (e.key === 'Tab' && idx === items.length - 1 && !e.shiftKey && lastFocusElementId) {
      e.preventDefault()
      const closeButton = document.querySelector(`[data-id="${lastFocusElementId}"]`) as HTMLElement
      closeButton?.focus()
    }
  }

  return (
    <>
      {items.map((item, idx) => {
        const isOpen = idx === openItemIdx
        const iconName = isOpen ? 'rounded_arrow_up' : 'rounded_arrow_down'

        return (
          <StyledAccordionItem
            key={idx}
            aria-expanded={isOpen}
            onClick={() => toggleOpen(idx)}
            data-id={typeof makeDataId === 'function' ? makeDataId(item) : ''}
            onKeyDown={(e) => handleKeyDown(e, idx)}
          >
            <StyledAccordionItemHead>
              <StyledAccordionItemHeadText>{item.title}</StyledAccordionItemHeadText>
              <StyledAccordionItemHeadIcon w={14} h={14} name={iconName} ariaHidden />
            </StyledAccordionItemHead>
            <StyledAccordionItemBody
              role="region"
              aria-live="polite"
              aria-hidden={!isOpen}
              isOpen={isOpen}
            >
              <StyledAccordionItemBodyInner>{item.content}</StyledAccordionItemBodyInner>
            </StyledAccordionItemBody>
          </StyledAccordionItem>
        )
      })}
    </>
  )
}

export default Accordion
